<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  url: { type: String, required: true },
  preview: { type: Object, default: () => {} },
  view: { type: String, default: 'default' },
  title: { type: String, default: '' },
});
const isShorts = computed(() => /\/shorts\//.test(props.url));
const visible = ref(false);

// Extract YouTube ID from many URL shapes
const videoId = computed(() => {
  const u = props.url?.trim() || '';
  // youtu.be/<id>
  let m = u.match(/youtu\.be\/([A-Za-z0-9_-]{6,})/);
  if (m?.[1]) return m[1];
  // youtube.com/watch?v=<id>
  m = u.match(/[?&]v=([A-Za-z0-9_-]{6,})/);
  if (m?.[1]) return m[1];
  // youtube.com/embed/<id>
  m = u.match(/\/embed\/([A-Za-z0-9_-]{6,})/);
  if (m?.[1]) return m[1];
  // youtube.com/shorts/<id>
  m = u.match(/\/shorts\/([A-Za-z0-9_-]{6,})/);
  if (m?.[1]) return m[1];
  return '';
});

// YouTube preview (webp first, jpg fallback)
const ytPreviewWebp = computed(() =>
  videoId.value
    ? `https://i.ytimg.com/vi_webp/${videoId.value}/hqdefault.webp`
    : '',
);
const ytPreviewJpg = computed(() =>
  videoId.value ? `https://i.ytimg.com/vi/${videoId.value}/hqdefault.jpg` : '',
);

// Pick which preview to show (custom > YouTube)
const previewSrc = ref('');
previewSrc.value = props?.preview?.src || ytPreviewWebp.value;

const previewIsPortrait = ref(false);

function onPreviewError(e) {
  // If webp fails, try jpg; if that fails and it equals custom, leave as is.
  if (e?.target?.src === ytPreviewWebp.value && ytPreviewJpg.value) {
    e.target.src = ytPreviewJpg.value;
    return;
  }
}

// Embed URL with autoplay when dialog visible
const embedSrc = computed(() => {
  const id = videoId.value;
  if (!id) return '';
  // modestbranding to reduce overlays; rel=0 (YT may still show related from same channel)
  return `https://www.youtube.com/embed/${id}?autoplay=1&modestbranding=1&rel=0`;
});

function open() {
  // Ensure we display some preview even if none provided and id resolved later
  if (!props?.preview?.src && !previewSrc.value)
    previewSrc.value = ytPreviewWebp.value || ytPreviewJpg.value;
  visible.value = true;
}
function close() {
  visible.value = false; // iframe unmounts (v-if), playback stops
}
// Orientation auto
const effectiveOrientation = computed(() => {
  if (isShorts.value) return 'portrait';
  if (previewIsPortrait.value) return 'portrait';
  return 'landscape';
});
</script>

<template>
  <figure :class="['vp', `vp--${view}`]">
    <div
      class="vp__media"
      role="button"
      tabindex="0"
      @click="open"
      @keyup.enter.space="open"
    >
      <img
        v-if="previewSrc"
        class="vp__img"
        :src="previewSrc"
        :alt="title || 'Video preview'"
        @error="onPreviewError"
      />
      <img
        v-else-if="ytPreviewWebp"
        class="vp__img"
        :src="ytPreviewWebp"
        :alt="title || 'Video preview'"
        @error="onPreviewError"
      />
      <div class="vp__overlay">
        <button type="button" class="vp__play js-button-play">
          <div class="vp__play-icon">
            <svg
              width="72"
              height="72"
              viewBox="0 0 72 72"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_1202_283637)">
                <circle cx="36" cy="36" r="36" fill="#ED1C24" />
                <path
                  d="M42.4091 35.0219C43.197 35.4566 43.197 36.5434 42.4091 36.9781L31.7727 42.847C30.9848 43.2817 30 42.7383 30 41.8689L30 30.1311C30 29.2617 30.9848 28.7183 31.7727 29.153L42.4091 35.0219Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_1202_283637">
                  <rect width="72" height="72" rx="36" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </div>
        </button>
      </div>
    </div>
    <figcaption
      v-if="title && view === 'card'"
      class="vp__title"
      v-html="title"
    ></figcaption>
    <Dialog
      v-model:visible="visible"
      modal
      :dismissable-mask="true"
      header=" "
      :class="['vp__dialog', `vp__dialog--${effectiveOrientation}`]"
      @hide="close"
    >
      <div v-if="visible" class="vp__player">
        <iframe
          v-if="embedSrc"
          :src="embedSrc"
          title="YouTube video player"
          frameborder="0"
          allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
          allowfullscreen
          :class="['vp__iframe', `vp__iframe--${effectiveOrientation}`]"
        ></iframe>
        <div v-else class="vp__fallback">Invalid YouTube URL</div>
      </div>
    </Dialog>
  </figure>
</template>

<style scoped lang="scss">
.vp {
  $parent: &;
  display: block;
  width: 100%;
  &__overlay {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
  }
  &__play {
    border-radius: 50%;
    align-items: center;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    &:hover {
      circle {
        fill: $white;
      }
      path {
        fill: $primary;
      }
    }
    &:focus,
    &:focus-visible {
      outline: 1px solid $focus;
      outline-offset: 4px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(outline-width, 1);
        @include responsive-spacing(outline-offset, 4);
      }
    }

    &:disabled {
      cursor: wait;
    }
    &:active {
      circle {
        fill: $white;
      }
      path {
        fill: $black;
      }
    }
    &-icon {
      align-items: center;
      display: flex;
      justify-content: center;
      height: 48px;
      width: 48px;
      @include media-breakpoint-up(sm) {
        height: 72px;
        width: 72px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(height, 72);
        @include responsive-spacing(width, 72);
      }
    }
  }
  &__media {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 4);
    }
  }
  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  &__title {
    @include h3();
    color: $white;
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 16px;
    width: 100%;
    @include media-breakpoint-up(sm) {
      @include h4();
      padding: 24px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(padding, 24);
    }
  }
  &__iframe {
    width: 100%;
    height: 100%;
    display: block;
    border: 0;
    border-radius: 8px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
    &--portrait {
      aspect-ratio: 9/16;
      height: 80vh;
      width: calc(9 * 80vh / 16);
    }
    &--landscape {
      aspect-ratio: 16/9;
      height: auto;
      width: calc(100vw - 32px);
      @include media-breakpoint-up(md) {
        width: calc(90vw - 32px);
      }
      @include media-breakpoint-up(xl) {
        width: calc(90vw - px-to-vw(32));
      }
    }
  }
  &__fallback {
    display: grid;
    place-items: center;
    width: 100%;
    height: 100%;
  }
  &--card {
    position: relative;
    #{$parent} {
      &__overlay {
        background: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0) 18.11%,
          rgba(0, 0, 0, 0.8) 83.21%
        );
      }
      &__media {
        border-radius: 4px;
        aspect-ratio: 312/390;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(border-radius, 4);
        }
      }
    }
  }
  &--carousel {
    height: 100%;
    width: auto;
    #{$parent} {
      &__media {
        height: 100%;
        width: 100%;
      }
      &__img {
        border-radius: 8px;
      }
    }
  }
}
</style>
